import type { PaginatedResult } from "$routes/inventory/_lib/types";
import { type ApolloClient, gql, useApolloClient } from "@apollo/client";
import { useInventoryTableFilterContext } from "./inventory-context";

export interface InventorySummary {
  id: string;
  name: string;
  quantity?: number;
  expiryDate?: string;
  sku?: { externalReferences: ReadonlyArray<{ origin: string }> } | null;
}

export interface InventorySearchResults {
  inventorySearchTmp: PaginatedResult<InventorySummary>;
}

export const INVENTORY_SEARCH_QUERY = gql`
  query inventoryLots(
    $name: String
    $facilityId: String!
    $locationId: String
    $itemType: InventoryItemType
    $orderBy: InventorySearchOrderProperty
    $orderDirection: OrderDirection
    $pageSize: Int
    $pageNumber: Int
  ) {
    inventorySearchTmp(
      input: {
        name: $name
        facilityId: $facilityId
        locationId: $locationId
        itemType: $itemType
        lot: { quantity: { value: 0, comparator: GT } }
        quantity: { value: 0, comparator: GT }
      }
      options: {
        order: { by: $orderBy, direction: $orderDirection }
        pageSize: $pageSize
        pageNumber: $pageNumber
      }
    ) {
      totalCount
      edges {
        node {
          id
          name
          quantity
          expiryDate
          supplyLevel
          sku {
            id
            externalReferences {
              origin
            }
          }
        }
      }
    }
  }
`;

const GET_LOTS_BY_SKU_ID_QUERY = gql`
  query getLotsBySkuIdQuery($skuId: String!, $facilityId: String!, $locationId: String) {
    getLotsBySkuId(
      input: {
        skuId: $skuId
        facilityId: $facilityId
        locationId: $locationId
        quantity: { value: 0, comparator: GT }
      }
    ) {
      id
      locationId
      quantity
      expiryDate
    }
  }
`;

export interface Lot {
  expiryDate: Date;
  id: string;
  locationId: string;
  name: string;
  quantity: number;
  skuId: string;
  externalReferences?: ReadonlyArray<{ origin: string }>;
}

interface LotsBySkuIdSearchResults {
  getLotsBySkuId: Lot[];
}

type GetLotBySkuIdArgs = {
  skuId: string;
  facilityId: string;
  locationId?: string | null;
};

export async function getLotBySkuId(
  client: ApolloClient<unknown>,
  { skuId, facilityId, locationId = null }: GetLotBySkuIdArgs,
) {
  return client.query<LotsBySkuIdSearchResults>({
    query: GET_LOTS_BY_SKU_ID_QUERY,
    variables: { skuId, facilityId, locationId },
  });
}

type ResetCacheArgs =
  | {
      facilityId: string;
      skuIdToClearChildLotsFor: string;
      locationId?: string | null;
    }
  | {
      facilityId: string;
      clearLotsForAllSkus: true;
    };

export async function resetTableCache(
  client: ApolloClient<unknown>,
  args: ResetCacheArgs,
) {
  return client.refetchQueries({
    updateCache(cache) {
      cache.evict({ fieldName: "inventorySearchTmp" });

      if ("clearLotsForAllSkus" in args) {
        // Nest this conditional so the type narrowing in the line above works
        if (args.clearLotsForAllSkus) {
          cache.evict({
            fieldName: "getLotsBySkuId",
          });
        }
        return;
      }

      const { facilityId, skuIdToClearChildLotsFor, locationId = null } = args;

      // When evicting here we need these args to match exactly those passed to the resolver, not those we pass to our query
      // as that is what generates the cache key
      const getLotBySkuArgs = {
        skuId: skuIdToClearChildLotsFor,
        facilityId,
        locationId,
        quantity: { value: 0, comparator: "GT" },
      };
      cache.evict({
        fieldName: "getLotsBySkuId",
        args: {
          input: getLotBySkuArgs,
        },
      });

      // If evicting for a specific location also evict the facility wide query as that will be out of date too
      if (locationId) {
        cache.evict({
          fieldName: "getLotsBySkuId",
          args: {
            input: { ...getLotBySkuArgs, locationId: null },
          },
        });
      }
    },
  });
}

export function useResetTableCache() {
  const client = useApolloClient();
  const { incrementRefreshKey } = useInventoryTableFilterContext();

  return async (args: ResetCacheArgs) => {
    const output = await resetTableCache(client, args);

    // If we've cleared the apollo cache in most cases we will also need to ensure the nested table rows are updated too.
    // If that's not done we risk still showing stale lot data even if the apollo cache has been updated (due to the way
    // the table is implemented). Force the refresh here to make it less likely that we don't forget to do it elsewhere.
    incrementRefreshKey();

    return output;
  };
}
